import {
  AccountData,
  Follower,
  MembershipPlan,
  MembershipPromoCode,
  MembershipPromoCodeSource,
  MembershipPromoCodeStatus,
} from "@markit/common.types";
import { API } from "../API";
import {
  getUserData,
  getUserFollowerData,
  getUserFollowersRef,
  getUserMembershipPromoCodesRef,
} from "./FirebaseUtils";
import { getCountFromServer, getDocs, query, where } from "../firebase";

export const changeMembershipPlan = async (
  newMembershipPlan: MembershipPlan,
  uid: string
) => {
  try {
    await API.user.updateMembershipPlan({
      uid,
      newMembershipPlan,
    });
  } catch (error: any) {
    alert("Error updating membership plan: " + error.message);
  }
};

export const createMembershipPlan = async (
  newMembershipPlan: MembershipPlan,
  uid: string
) => {
  try {
    await API.user.addMembershipPlan({
      uid,
      newMembershipPlan,
    });
  } catch (error: any) {
    alert("Error creating membership plan: " + error.message);
  }
};

export const deleteMembershipPlan = async (
  membershipPlanId: string,
  uid: string
) => {
  try {
    await API.user.deleteMembershipPlan({
      uid,
      membershipPlanId,
    });
  } catch (error: any) {
    alert("Error deleting membership plan: " + error.message);
  }
};

export const getMembershipPromoCodeUsageCount = async (
  creatorUid: string,
  promoCodeId: string
): Promise<number> => {
  try {
    const userFollowersRef = await getUserFollowersRef(creatorUid);
    const query_ = query(
      userFollowersRef,
      where("membershipPromoCodeId", "==", promoCodeId)
    );
    const snapshot = await getCountFromServer(query_);
    return snapshot.data().count;
  } catch (error) {
    console.error("Error fetching promo code usage: ", error);
    return 0;
  }
};

// This function fetches all the members that have used a specific group membership promo code
export const getMembersFromGroupPromoCode = async (
  creatorUid: string,
  promoCodeId: string
): Promise<Follower[]> => {
  try {
    const userFollowerRef = getUserFollowersRef(creatorUid);
    const userFollowerQuery = query(
      userFollowerRef,
      where("membershipPromoCodeId", "==", promoCodeId)
    );

    const querySnapshot = await getDocs(userFollowerQuery);

    return querySnapshot.docs.map((doc) => doc.data() as Follower);
  } catch (error) {
    console.error("Error fetching members from promo code: ", error);
    return [];
  }
};

export const checkActiveMembershipPromoCodeAliasExists = async (
  uid: string,
  alias: string
): Promise<boolean> => {
  try {
    const userFollowersRef = await getUserMembershipPromoCodesRef(uid);
    const query_ = query(
      userFollowersRef,
      where("alias", "==", alias.toUpperCase()),
      where("active", "==", true)
    );
    const snapshot = await getCountFromServer(query_);
    return snapshot.data().count > 0;
  } catch (error) {
    console.error("Error checking promo code alias:", error);
    return false;
  }
};

export const checkValidMembershipPromoCode = async (
  creatorUid: string,
  promoCodeAlias: string,
  selectedMembershipPlanId: string
): Promise<{
  status: MembershipPromoCodeStatus;
  promoCode: MembershipPromoCode | undefined;
  groupPlanOwner: AccountData | undefined;
}> => {
  const memershipPromoCodesRef = await getUserMembershipPromoCodesRef(
    creatorUid
  );
  const query_ = query(
    memershipPromoCodesRef,
    where("alias", "==", promoCodeAlias),
    where("active", "==", true)
  );
  const snapshot = await getDocs(query_);
  if (!snapshot.empty) {
    const promoCode = snapshot.docs[0].data();
    const count = await getMembershipPromoCodeUsageCount(
      creatorUid,
      promoCode.id
    );
    if (promoCode.active && count < promoCode.maxRedemptions) {
      // also check if it's a system promo code and if you're redeeming it for the same plan
      if (promoCode.source === MembershipPromoCodeSource.SYSTEM) {
        const followerData = await getUserFollowerData(
          creatorUid,
          promoCode.groupOwnerUid
        );
        if (followerData?.membershipPlanId === selectedMembershipPlanId) {
          const groupOwnerData = await getUserData(followerData.uid);
          return {
            status: MembershipPromoCodeStatus.PROMO_CODE_VALID,
            promoCode: promoCode,
            groupPlanOwner: groupOwnerData,
          };
        } else {
          return {
            status: MembershipPromoCodeStatus.PROMO_CODE_WRONG_PLAN,
            promoCode: undefined,
            groupPlanOwner: undefined,
          };
        }
      } else {
        return {
          status: MembershipPromoCodeStatus.PROMO_CODE_VALID,
          promoCode: promoCode,
          groupPlanOwner: undefined,
        };
      }
    }
    return {
      status: MembershipPromoCodeStatus.PROMO_CODE_EXPIRED,
      promoCode: undefined,
      groupPlanOwner: undefined,
    };
  }
  return {
    status: MembershipPromoCodeStatus.PROMO_CODE_NOT_FOUND,
    promoCode: undefined,
    groupPlanOwner: undefined,
  };
};
